import fs from "node:fs";
import type { LockProvider, MutexOption, RetryOptions } from "./interfaces";

export const defaultStaleDuration = 2 * 60 * 1000; // two minutes

interface ActiveLock {
    lockPath: string;
    timer: NodeJS.Timeout;
    released: boolean;
}

interface NormalizedRetry {
    maxAttempts: number;
    minTimeout: number;
    maxTimeout: number;
    randomize: boolean;
    maxRetryTime: number;
}

function normalizeRetry(retries: number | RetryOptions | undefined): NormalizedRetry {
    if (retries === undefined) {
        // Default: retry forever with jitter
        return {
            maxAttempts: Number.POSITIVE_INFINITY,
            minTimeout: 100,
            maxTimeout: 2000,
            randomize: true,
            maxRetryTime: Number.POSITIVE_INFINITY
        };
    }
    if (typeof retries === "number") {
        return {
            maxAttempts: retries,
            minTimeout: 1000,
            maxTimeout: Number.POSITIVE_INFINITY,
            randomize: false,
            maxRetryTime: Number.POSITIVE_INFINITY
        };
    }
    const maxAttempts = retries.forever ? Number.POSITIVE_INFINITY : (retries.retries ?? 10);
    return {
        maxAttempts,
        minTimeout: retries.minTimeout ?? 1000,
        maxTimeout: retries.maxTimeout ?? Number.POSITIVE_INFINITY,
        randomize: retries.randomize ?? false,
        maxRetryTime: retries.maxRetryTime ?? Number.POSITIVE_INFINITY
    };
}

function retryDelay(r: NormalizedRetry, attempt: number): number {
    let d = Math.min(r.minTimeout * 2 ** attempt, r.maxTimeout);
    if (r.randomize) {
        d = Math.floor((d * (1 + Math.random())) / 2);
    }
    return d;
}

function lockPath(fileToLock: string): string {
    return `${fileToLock}.lock`;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function isStale(lp: string, staleDuration: number): Promise<boolean> {
    try {
        const stat = await fs.promises.stat(lp);
        return Date.now() - stat.mtimeMs > staleDuration;
    } catch {
        return true; // directory gone → treat as stale
    }
}

export class NativeLockProvider implements LockProvider {
    private activeLocks = new Map<number, ActiveLock>();
    private nextLockId = 0;
    private exitHandlerRegistered = false;

    private cleanupLocksOnExit = () => {
        for (const [, entry] of this.activeLocks) {
            if (!entry.released) {
                entry.released = true;
                clearInterval(entry.timer);
                try {
                    fs.rmSync(entry.lockPath, { recursive: true, force: true });
                } catch {
                    /* best-effort cleanup */
                }
            }
        }
        this.activeLocks.clear();
    };

    private ensureExitHandler(): void {
        if (this.exitHandlerRegistered) return;
        this.exitHandlerRegistered = true;
        process.on("exit", this.cleanupLocksOnExit);
    }

    private async acquireLock(
        lp: string,
        stale: number,
        retry: NormalizedRetry,
        update: number,
        onCompromised?: (err: Error) => void
    ): Promise<number> {
        const startTime = Date.now();
        let attempt = 0;

        for (;;) {
            try {
                await fs.promises.mkdir(lp);

                // — Lock acquired — register exit cleanup + start refresh timer
                this.ensureExitHandler();
                const lockId = this.nextLockId++;
                const timer = setInterval(() => {
                    const entry = this.activeLocks.get(lockId);
                    if (!entry || entry.released) return;
                    try {
                        const now = new Date();
                        fs.utimesSync(lp, now, now);
                    } catch (err) {
                        if (onCompromised) {
                            onCompromised(err as Error);
                        }
                    }
                }, update);
                timer.unref();
                this.activeLocks.set(lockId, { lockPath: lp, timer, released: false });
                return lockId;
            } catch (err: unknown) {
                const code = (err as NodeJS.ErrnoException).code;

                let isSwallowedEperm = false;
                if (code === "EEXIST") {
                    // Normal contention — lock dir already exists
                } else if (code === "EPERM") {
                    // On Windows, mkdir can throw EPERM when the lock
                    // path already exists as a regular file (e.g.
                    // leftover from a different locking library).
                    // Disambiguate from genuine permission issues by
                    // checking whether something actually exists at lp.
                    try {
                        fs.statSync(lp);
                        // Something exists → treat like EEXIST below
                    } catch {
                        // Do not throw err here on EPERM stat failure.
                        // On Windows, concurrent mkdir can throw EPERM and stat can concurrently fail or be denied.
                        // We swallow this so the backoff retry logic can proceed.
                        isSwallowedEperm = true;
                    }
                } else {
                    throw err;
                }

                if (!isSwallowedEperm) {
                    // Lock directory exists — check staleness
                    if (await isStale(lp, stale)) {
                        try {
                            await fs.promises.rm(lp, { recursive: true, force: true });
                        } catch {
                            /* another process may have removed it */
                        }
                        continue; // retry immediately after removing a stale lock
                    }
                }

                // Not stale — obey retry limits
                if (attempt >= retry.maxAttempts) {
                    throw new Error("Lock file is already being held");
                }
                if (Date.now() - startTime >= retry.maxRetryTime) {
                    throw new Error("Lock file is already being held");
                }

                await sleep(retryDelay(retry, attempt));
                attempt++;
            }
        }
    }

    private releaseLock(lockId: number): void {
        const entry = this.activeLocks.get(lockId);
        if (entry) {
            entry.released = true;
            clearInterval(entry.timer);
            this.activeLocks.delete(lockId);
            fs.rmSync(entry.lockPath, { recursive: true, force: true });
        }
    }

    public async acquire(options: MutexOption): Promise<() => Promise<void>> {
        const { fileToLock, stale = defaultStaleDuration, update, retries, onCompromised } = options;

        const retry = normalizeRetry(retries);
        const updateInterval = update ?? Math.floor(stale / 2);
        const lp = lockPath(fileToLock);

        const lockId = await this.acquireLock(lp, stale, retry, updateInterval, onCompromised);

        return async () => {
            this.releaseLock(lockId);
        };
    }

    public async isLocked(fileToLock: string): Promise<boolean> {
        const lp = lockPath(fileToLock);
        try {
            await fs.promises.access(lp);
            // Lock dir exists — but is it stale (orphaned)?
            return !(await isStale(lp, defaultStaleDuration));
        } catch {
            return false;
        }
    }

    public async drainPendingLocks(): Promise<void> {}
}
